import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OperatorPageShell, Button } from '@/components';
import { useAuthFetch } from '@/hooks';
import { ROUTES, practicePath } from '../../routes.ts';
import { TABLE_BTN } from './operatorTableConstants.ts';
import { Th } from './operatorTableHelpers.tsx';
import panel from './operatorPanel.module.css';
import styles from './OperatorOrganizations.module.css';
import { ORGANIZATION_TYPES } from '@dermalink/shared';
import type {
  CreateOrganizationResponse,
  OperatorDoctorListResponse,
  OperatorDoctorSummary,
  OrganizationDoctorSummary,
  OrganizationListResponse,
  OrganizationType,
  OrganizationWithDoctors,
} from '@dermalink/shared';

const TYPE_LABELS: Record<OrganizationType, string> = {
  clinic:   'Kliniek',
  hospital: 'Ziekenhuis',
};

export function OperatorOrganizationsPanel() {
  const authFetch = useAuthFetch();
  const [data, setData]       = useState<OrganizationListResponse | null>(null);
  const [doctors, setDoctors] = useState<OperatorDoctorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  const [name, setName]       = useState('');
  const [type, setType]       = useState<OrganizationType>('clinic');
  const [address, setAddress] = useState('');
  const [createState, setCreateState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      authFetch('/operator/organizations').then((r) => r.ok ? r.json() : Promise.reject()),
      authFetch('/operator/doctors').then((r) => r.ok ? r.json() : Promise.reject()),
    ])
      .then(([orgData, doctorData]: [OrganizationListResponse, OperatorDoctorListResponse]) => {
        setData(orgData);
        setDoctors(doctorData.doctors);
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateState('loading');
    try {
      const res = await authFetch('/operator/organizations', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name, type, address }),
      });
      if (!res.ok) throw new Error();
      await res.json() as CreateOrganizationResponse;
      setCreateState('success');
      setName('');
      setType('clinic');
      setAddress('');
      load();
    } catch {
      setCreateState('error');
    }
  }

  // Which organization (if any) each doctor currently belongs to, so the
  // "add doctor" picker can flag a reassignment before it silently happens.
  const orgByDoctor = new Map<string, string>();
  data?.organizations.forEach((o) => o.doctors.forEach((d) => orgByDoctor.set(d.cognito_sub, o.name)));

  return (
    <>
      <div className={panel.section}>
        <h2 className={panel.sectionHeading}>Organisatie aanmaken</h2>
        <form onSubmit={handleCreate} className={panel.inlineForm}>
          <input
            type="text"
            required
            placeholder="Naam"
            value={name}
            onChange={(e) => { setName(e.target.value); setCreateState('idle'); }}
            className={panel.field}
            style={{ minWidth: '200px' }}
          />
          <select
            value={type}
            onChange={(e) => { setType(e.target.value as OrganizationType); setCreateState('idle'); }}
            className={panel.field}
            style={{ width: '160px' }}
          >
            {ORGANIZATION_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
          <input
            type="text"
            required
            placeholder="Adres"
            value={address}
            onChange={(e) => { setAddress(e.target.value); setCreateState('idle'); }}
            className={panel.field}
            style={{ minWidth: '240px' }}
          />
          <Button type="submit" variant="primary" disabled={createState === 'loading'}>
            {createState === 'loading' ? 'Bezig…' : 'Organisatie aanmaken'}
          </Button>
          {createState === 'success' && <span className={panel.successText}>Aangemaakt.</span>}
          {createState === 'error' && <span className={panel.errorText}>Aanmaken mislukt.</span>}
        </form>
      </div>

      <div className={panel.section}>
        {loading && <p className="text-muted status-message">Laden…</p>}
        {error   && <p className="text-muted status-message">Kon organisaties niet laden.</p>}

        {data && !loading && (
          <div className={panel.tableWrap}>
            <table className={panel.table}>
              <thead>
                <tr>
                  <Th>Naam</Th>
                  <Th>Type</Th>
                  <Th>Adres</Th>
                  <Th>Deeplink</Th>
                  <Th>Artsen</Th>
                  <Th>Praktijkbeheerders</Th>
                  <Th>Betalingen</Th>
                </tr>
              </thead>
              <tbody>
                {data.organizations.map((o) => (
                  <OrganizationRow key={o.id} org={o} doctors={doctors} orgByDoctor={orgByDoctor} onChanged={load} />
                ))}
                {data.organizations.length === 0 && (
                  <tr><td colSpan={7} className={panel.emptyCell}>Geen organisaties gevonden.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export function OperatorOrganizations() {
  return (
    <OperatorPageShell maxWidth="1400px">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to={ROUTES.operator} className={panel.mutedText}>← Beheerder</Link>
        <h1 className="page-heading" style={{ margin: 0 }}>Organisaties</h1>
      </div>
      <OperatorOrganizationsPanel />
    </OperatorPageShell>
  );
}

function OrganizationRow({
  org, doctors, orgByDoctor, onChanged,
}: {
  org: OrganizationWithDoctors;
  doctors: OperatorDoctorSummary[];
  orgByDoctor: Map<string, string>;
  onChanged: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const deeplinkPath = practicePath(org.slug);
  const deeplink = `${window.location.origin}${deeplinkPath}`;

  async function handleCopyDeeplink() {
    await navigator.clipboard.writeText(deeplink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const assignedSubs = new Set(org.doctors.map((d) => d.cognito_sub));
  const available = doctors.filter((d) => !assignedSubs.has(d.cognito_sub));
  const adminCandidates = org.doctors.filter((d) => !d.is_admin);

  return (
    <tr>
      <td className={panel.td}>{org.name}</td>
      <td className={panel.td}>{TYPE_LABELS[org.type]}</td>
      <td className={panel.td}>{org.address}</td>
      <td className={panel.td}>
        <div className={styles.deeplink}>
          <a href={deeplink} target="_blank" rel="noopener noreferrer">{deeplinkPath}</a>
          <button type="button" className={panel.linkButton} onClick={() => { void handleCopyDeeplink(); }}>
            {copied ? 'Gekopieerd' : 'Kopieer'}
          </button>
        </div>
      </td>
      <td className={panel.td}>
        <PeopleCell
          people={org.doctors}
          emptyLabel="Geen artsen"
          addLabel="Arts toevoegen"
          removePath={(sub) => `/operator/organizations/${org.id}/doctors/${sub}`}
          onChanged={onChanged}
          renderAddForm={(close) => (
            <AddDoctorForm orgId={org.id} available={available} orgByDoctor={orgByDoctor} onDone={() => { close(); onChanged(); }} onCancel={close} />
          )}
        />
      </td>
      <td className={panel.td}>
        <PeopleCell
          people={org.doctors.filter((d) => d.is_admin)}
          emptyLabel={org.doctors.length ? 'Geen beheerders' : 'Voeg eerst een arts toe'}
          addLabel={adminCandidates.length ? 'Beheerder toevoegen' : null}
          removePath={(sub) => `/operator/organizations/${org.id}/admins/${sub}`}
          onChanged={onChanged}
          renderAddForm={(close) => (
            <AddAdminForm orgId={org.id} candidates={adminCandidates} onDone={() => { close(); onChanged(); }} onCancel={close} />
          )}
        />
      </td>
      <td className={panel.td}>
        <PaymentModeCell org={org} />
      </td>
    </tr>
  );
}

/**
 * Read-only payment routing. De Huidspecialisten (Dermalink's own practice) takes payments
 * via the Dermalink account; every other practice only through its own Mollie account, and
 * patients can book it once that account is linked and ready.
 */
function PaymentModeCell({ org }: { org: OrganizationWithDoctors }) {
  const { payment_mode, mollie, open_checkouts } = org.payments;
  if (payment_mode === 'platform') {
    return (
      <div className={styles.people}>
        <strong>Via Dermalink</strong>
        <span className={panel.mutedText}>Betalingen op het Dermalink-account</span>
      </div>
    );
  }
  const openOnDermalink = open_checkouts.platform;
  return (
    <div className={styles.people}>
      <strong>Eigen Mollie-account</strong>
      {!mollie.connected && <span className={panel.errorText}>Niet gekoppeld — niet boekbaar voor patiënten</span>}
      {mollie.connected && !mollie.can_receive_payments && (
        <span className={panel.errorText}>{mollie.account?.name ?? 'Gekoppeld'} — wacht op goedkeuring Mollie, niet boekbaar</span>
      )}
      {mollie.connected && mollie.can_receive_payments && (
        <span className={panel.mutedText}>{mollie.account?.name ?? 'Gekoppeld'} — klaar voor betalingen</span>
      )}
      {openOnDermalink > 0 && (
        <span className={panel.mutedText}>{openOnDermalink} openstaande {openOnDermalink === 1 ? 'betaling' : 'betalingen'} nog via Dermalink</span>
      )}
    </div>
  );
}

/** Names with a remove button each, and an add form that stays collapsed until asked for. */
function PeopleCell({
  people, emptyLabel, addLabel, removePath, onChanged, renderAddForm,
}: {
  people: { cognito_sub: string; first_name: string | null; last_name: string | null }[];
  emptyLabel: string;
  /** null hides the add link, e.g. when nobody is left to add. */
  addLabel: string | null;
  removePath: (cognitoSub: string) => string;
  onChanged: () => void;
  renderAddForm: (close: () => void) => React.ReactNode;
}) {
  const authFetch = useAuthFetch();
  const [adding, setAdding]           = useState(false);
  const [removingSub, setRemovingSub] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState(false);

  async function handleRemove(cognitoSub: string) {
    setRemovingSub(cognitoSub);
    setRemoveError(false);
    try {
      const res = await authFetch(removePath(cognitoSub), { method: 'DELETE' });
      if (!res.ok) throw new Error();
      onChanged();
    } catch {
      setRemoveError(true);
    } finally {
      setRemovingSub(null);
    }
  }

  return (
    <div className={styles.people}>
      {people.map((p) => {
        const name = `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
        return (
          <div key={p.cognito_sub} className={styles.person}>
            {name ? <span>{name}</span> : <span className={panel.mutedText}>Onvolledig profiel</span>}
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => { void handleRemove(p.cognito_sub); }}
              disabled={removingSub === p.cognito_sub}
              aria-label={`${name || 'Persoon'} verwijderen`}
              title="Verwijderen"
            >
              {removingSub === p.cognito_sub ? '…' : '×'}
            </button>
          </div>
        );
      })}
      {people.length === 0 && <span className={panel.mutedText}>{emptyLabel}</span>}
      {removeError && <span className={panel.errorText}>Verwijderen mislukt</span>}
      {adding
        ? renderAddForm(() => setAdding(false))
        : addLabel && <button type="button" className={`${panel.linkButton} ${styles.addLink}`} onClick={() => setAdding(true)}>+ {addLabel}</button>}
    </div>
  );
}

function AddDoctorForm({
  orgId, available, orgByDoctor, onDone, onCancel,
}: {
  orgId: number;
  available: OperatorDoctorSummary[];
  orgByDoctor: Map<string, string>;
  onDone: () => void;
  onCancel: () => void;
}) {
  const authFetch = useAuthFetch();
  const [selected, setSelected] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(false);
    try {
      const res = await authFetch(`/operator/organizations/${orgId}/doctors`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ cognito_sub: selected }),
      });
      if (!res.ok) throw new Error();
      onDone();
    } catch {
      setError(true);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.addForm}>
      <select value={selected} onChange={(e) => setSelected(e.target.value)} className={`${panel.field} ${panel.fieldSm}`} autoFocus>
        <option value="">Kies een arts…</option>
        {available.map((d) => {
          const currentOrg = orgByDoctor.get(d.cognito_sub);
          return (
            <option key={d.cognito_sub} value={d.cognito_sub}>
              {d.first_name} {d.last_name}{currentOrg ? ` (nu: ${currentOrg})` : ''}
            </option>
          );
        })}
      </select>
      <div className={styles.addActions}>
        <Button type="submit" variant="primary" style={TABLE_BTN} disabled={!selected || saving}>{saving ? '…' : 'Toevoegen'}</Button>
        <button type="button" className={panel.linkButton} onClick={onCancel}>Annuleren</button>
      </div>
      {error && <span className={panel.errorText}>Toevoegen mislukt</span>}
    </form>
  );
}

/** Administrators are picked from the practice's own doctors only (enforced server-side too). */
function AddAdminForm({
  orgId, candidates, onDone, onCancel,
}: {
  orgId: number;
  candidates: OrganizationDoctorSummary[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const authFetch = useAuthFetch();
  const [selected, setSelected] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(false);
    try {
      const res = await authFetch(`/operator/organizations/${orgId}/admins/${selected}`, { method: 'PUT' });
      if (!res.ok) throw new Error();
      onDone();
    } catch {
      setError(true);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.addForm}>
      <select value={selected} onChange={(e) => setSelected(e.target.value)} className={`${panel.field} ${panel.fieldSm}`} autoFocus>
        <option value="">Kies een arts van deze praktijk…</option>
        {candidates.map((d) => <option key={d.cognito_sub} value={d.cognito_sub}>{d.first_name} {d.last_name}</option>)}
      </select>
      <div className={styles.addActions}>
        <Button type="submit" variant="primary" style={TABLE_BTN} disabled={!selected || saving}>{saving ? '…' : 'Toevoegen'}</Button>
        <button type="button" className={panel.linkButton} onClick={onCancel}>Annuleren</button>
      </div>
      {error && <span className={panel.errorText}>Toevoegen mislukt</span>}
    </form>
  );
}
